<ng-template
  #priceLine
  let-label="label"
  let-value="value"
  let-originalValue="originalValue"
  let-labelContent="labelContent"
  let-bold="bold"
  let-borderBottom="borderBottom"
  let-category="category"
>
  <tr
    [attr.data-testid]="'price-breakdown-price-for-' + category"
    class="ps-xxsmall-b"
    [ngClass]="{ 'border-bottom': borderBottom }"
  >
    <th
      scope="row"
      class="align-left ps-xxsmall-b"
      [ngClass]="{ 'light-type': !bold, 'medium-type ms-xsmall-t': bold }"
    >
      @if (label) {
        {{ label }}
      } @else {
        <ng-container [ngTemplateOutlet]="labelContent" />
      }
    </th>
    <td
      data-testid="price-breakdown-price"
      class="align-right ps-xxsmall-b"
      [ngClass]="{ 'medium-type ms-xsmall-t': bold }"
    >
      @if (originalValue) {
        <span
          data-testid="price-breakdown-original-price"
          class="nordic-blue-900-text text-icon-primary strike-through pr-xxsmall-r"
          >{{ originalValue }}</span
        >
      }
      <span data-testid="price-breakdown-price">{{ value }}</span>
    </td>
  </tr>
</ng-template>

<ng-template #titleLine let-label="label" let-labelContent="labelContent" let-bold="bold">
  <tr class="ps-xxsmall-b">
    <th
      scope="row"
      class="align-left ps-xxsmall-y"
      [ngClass]="{ 'light-type': !bold, 'medium-type ms-xsmall-t': bold }"
    >
      @if (label) {
        {{ label }}
      } @else {
        <ng-container [ngTemplateOutlet]="labelContent" />
      }
    </th>
  </tr>
</ng-template>

<ng-template #expandedLine let-borderBottom="borderBottom">
  <tr class="ps-xxsmall-b" [class.border-bottom]="borderBottom">
    <ng-content />
  </tr>
</ng-template>

<fcom-modal
  [open]="open()"
  (openChange)="openChange.emit($event)"
  [title]="title()"
  [buttons]="ModalButtons.DONE"
  (close)="close()"
  data-testid="detailed-breakdown-per-passenger"
>
  @let breakdowns = breakdowns$ | async;
  @for (passenger of breakdowns; track passenger.id) {
    <div class="pr-xsmall-t pr-large-l pr-xxlarge-r">
      @if (passenger) {
        <div class="trigger flex">
          <div class="flex-1">
            <h3 class="font-heading-5 nordic-blue-50-bg surface-secondary font-title-small">
              <span class="flex-1 flex--self-center ps-xsmall-l medium-type" data-hj-suppress data-dd-privacy="hidden">
                {{ passenger?.title ? ('passenger.title.' + passenger?.title | lowercase | finLocalization) : '' }}
                {{ passenger?.firstName }} {{ passenger?.lastName }} ({{
                  'passenger.' + asPaxTypeKey(passenger.passengerTypeCode).toLowerCase() | finLocalization | titlecase
                }})
              </span>
            </h3>
          </div>
        </div>
        <div class="font-body-1 ps-xsmall-t content font-body-medium">
          <table class="fill price-breakdown-per-passenger">
            <caption class="is-vishidden">
              {{
                'purchase.priceBreakdown.detailedBreakdownPassenger' | finLocalization
              }}
            </caption>

            <thead>
              <tr>
                <th class="flex medium-type ps-xxsmall-b" scope="col" colspan="2">
                  {{ 'purchase.priceBreakdown.flights' | finLocalization }}
                </th>
              </tr>
            </thead>

            <tbody>
              <ng-container
                [ngTemplateOutlet]="priceLine"
                [ngTemplateOutletContext]="{
                  label: 'purchase.priceBreakdown.flights' | finLocalization,
                  originalValue:
                    passenger.prices?.originalBaseFare && passenger.discountReasonCode
                      ? (passenger.prices?.originalBaseFare | finPrice)
                      : undefined,
                  value:
                    showPoints() && passenger.prices?.totalPoints
                      ? ('award.points'
                        | finLocalization
                          : {
                              pointsAmount: +passenger.prices?.totalPoints.amount | finPoints,
                            })
                      : (passenger.prices?.baseFare | finPrice),
                  bold: false,
                }"
              />
              <ng-container
                [ngTemplateOutlet]="expandedLine"
                [ngTemplateOutletContext]="{ borderBottom: passenger.services.length }"
              >
                <ng-container>
                  <tr>
                    <td colspan="3">
                      <fin-expand
                        class="price-breakdown-modal"
                        [key]="'price-breakdown-expand-' + passenger?.id"
                        [id]="'price-breakdown-expand-' + passenger?.id"
                        #expand
                      >
                        <div class="trigger flex">
                          <div class="flex-1">
                            <span class="underline medium-type">
                              {{ 'purchase.review.taxTotal' | finLocalization }}
                            </span>
                          </div>
                          <div>
                            <span class="medium-type">{{
                              passenger.prices?.totalTax || {
                                amount: '0',
                                currencyCode: passenger.prices?.baseFare.currencyCode,
                              } | finPrice
                            }}</span>
                            <span class="ps-xsmall-l">
                              <fcom-icon
                                class="flex-1 flex--nogrow rotate flex--self-start icon-medium nordic-blue-900-text text-icon-primary"
                                [class.rotate-active]="expand.isOpen$ | async"
                                [name]="SvgLibraryIcon.CHEVRON_DOWN"
                              />
                            </span>
                          </div>
                        </div>
                        <div class="font-body-1 content font-body-medium">
                          <ul class="list-disc-style">
                            @for (tax of passenger.prices.taxes; track tax.code) {
                              <li>
                                <div class="flex flex--space-between">
                                  <span>
                                    {{
                                      'TAX_CODES.' + tax.code.slice(0, 2)
                                        | finLocalization
                                        | finEmpty: tax.code.slice(0, 2)
                                    }}
                                  </span>
                                  <span>
                                    {{ tax | finPrice }}
                                  </span>
                                </div>
                              </li>
                            }
                            @if (+passenger.prices.environmentalSurcharge?.amount > 0) {
                              <li>
                                <div class="flex flex--space-between">
                                  <span>
                                    {{ 'TAX_CODES.' + ENVIRONMENTAL_SURCHARGE | finLocalization }}
                                  </span>
                                  <span>
                                    {{ passenger.prices.environmentalSurcharge | finPrice }}
                                  </span>
                                </div>
                              </li>
                            }
                          </ul>
                        </div>
                      </fin-expand>
                    </td>
                  </tr>
                </ng-container>
              </ng-container>

              @for (fee of passenger.prices?.fees ?? []; track fee.code) {
                <ng-container
                  [ngTemplateOutlet]="priceLine"
                  [ngTemplateOutletContext]="{
                    label: 'purchase.review.' + fee.code | finLocalization,
                    value: fee | finPrice,
                    bold: false,
                  }"
                />
              }
              @if (passenger.hasOtherServices) {
                <ng-container
                  [ngTemplateOutlet]="titleLine"
                  [ngTemplateOutletContext]="{
                    label: 'purchase.priceBreakdown.travelExtras' | finLocalization,
                    bold: true,
                  }"
                />
              }
              @for (service of passenger.services; track service.id) {
                @if (service.count && !(service.id === Category.SAF)) {
                  <ng-container
                    [ngTemplateOutlet]="priceLine"
                    [ngTemplateOutletContext]="{
                      label:
                        service.key
                        | finLocalization
                          : {
                              seatCount: service.count,
                              mealCount: service.count,
                              baggageCount: service.count,
                              loungeCount: service.count,
                              travelComfortCount: service.count,
                              wifiCount: service.count,
                              coverCount: service.count,
                              petCount: service.count,
                              sportCount: service.count,
                              specialCount: service.count,
                              firearmCount: service.count,
                              medicCount: service.count,
                              specialNeedCount: service.count,
                              childCount: service.count,
                              bundleCount: service.count,
                              amount: service.count,
                            },
                      value: service.total | finPrice,
                      originalValue: service.originalTotal | finPrice,
                      bold: false,
                    }"
                  />
                }
              }
              @if (passenger.hasSaf) {
                <ng-container
                  [ngTemplateOutlet]="titleLine"
                  [ngTemplateOutletContext]="{
                    label: 'purchase.priceBreakdown.safContribution' | finLocalization,
                    bold: true,
                  }"
                />
              }
              @for (service of passenger.services; track service.id) {
                @if (service.count && service.id === Category.SAF) {
                  <ng-container
                    [ngTemplateOutlet]="priceLine"
                    [ngTemplateOutletContext]="{
                      label:
                        service.key
                        | finLocalization
                          : {
                              safCount: service.count,
                            },
                      value: service.total | finPrice,
                      originalValue: service.originalTotal | finPrice,
                      bold: false,
                      category: service.id,
                    }"
                  />
                }
              }
            </tbody>
            <tfoot class="border-top">
              <ng-container
                [ngTemplateOutlet]="priceLine"
                [ngTemplateOutletContext]="{
                  label: ('purchase.priceBreakdown.totalPrice' | finLocalization),
                  value:
                    showPoints() && passenger.prices?.totalPoints
                      ? ('award.points'
                          | finLocalization
                            : {
                                pointsAmount: +passenger.prices?.totalPoints.amount | finPoints,
                              }) +
                        '&nbsp;+&nbsp;' +
                        (passenger.total | finPrice)
                      : (passenger.total | finPrice),
                  bold: true,
                }"
              />
            </tfoot>
          </table>
        </div>
      }
    </div>
  }
</fcom-modal>
